import { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import CookieConsent from "@/components/CookieConsent";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import { orgSchemas } from "@/components/Seo";
import { trackEvent } from "@/utils/analytics";
import Home from "@/pages/Home";

const KisanKota = lazy(() => import("@/pages/KisanKota"));
const YeidaRules = lazy(() => import("@/pages/YeidaRules"));
const Ptl = lazy(() => import("@/pages/Ptl"));
const TransferMemorandum = lazy(() => import("@/pages/TransferMemorandum"));
const TransferCharges = lazy(() => import("@/pages/TransferCharges"));
const SectorPage = lazy(() => import("@/pages/SectorPage"));
const JewarImpact = lazy(() => import("@/pages/JewarImpact"));
const BuyingGuides = lazy(() => import("@/pages/BuyingGuides"));
const Listings = lazy(() => import("@/pages/Listings"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const AdminBlog = lazy(() => import("@/pages/AdminBlog"));
const LeadDashboard = lazy(() => import("@/pages/LeadDashboard"));
const AuthorityPlots = lazy(() => import("@/pages/AuthorityPlots"));
const SectorsHub = lazy(() => import("@/pages/SectorsHub"));
const Development = lazy(() => import("@/pages/Development"));
const InvestorGuide = lazy(() => import("@/pages/InvestorGuide"));
const KisanVsAuthority = lazy(() => import("@/pages/KisanVsAuthority"));
const PlotTransferProcess = lazy(() => import("@/pages/PlotTransferProcess"));
const RpsScheme = lazy(() => import("@/pages/RpsScheme"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("@/pages/TermsConditions"));
const FaqPage = lazy(() => import("@/pages/FaqPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const ScrollManager = () => {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            setTimeout(() => {
                document.getElementById(hash.replace("#", ""))?.scrollIntoView({ behavior: "smooth" });
            }, 400);
        } else {
            window.scrollTo(0, 0);
        }
    }, [pathname, hash]);
    return null;
};

const PageLoader = () => <div className="min-h-screen bg-bone" data-testid="page-loader" />;

function App() {
    useEffect(() => {
        const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
        let frame;
        const raf = (time) => {
            lenis.raf(time);
            frame = requestAnimationFrame(raf);
        };
        frame = requestAnimationFrame(raf);
        const locate = (el) => el.closest("[data-testid]")?.getAttribute("data-testid") || "unknown";
        const onClick = (e) => {
            const a = e.target.closest("a");
            if (!a) return;
            const href = a.getAttribute("href") || "";
            if (href.startsWith("tel:")) trackEvent("call_click", { link: href, button_location: locate(a) });
            else if (href.includes("wa.me")) trackEvent("whatsapp_click", { link: "whatsapp", button_location: locate(a) });
            else if (href.includes(".pdf")) trackEvent("investor_guide_download", { link: "pdf", button_location: locate(a) });
        };
        const onSubmit = (e) => {
            const f = e.target;
            if (f && f.tagName === "FORM") trackEvent("form_submit", { button_location: f.getAttribute("data-testid") || "form" });
        };
        document.addEventListener("click", onClick);
        document.addEventListener("submit", onSubmit);
        orgSchemas().forEach((s) => {
            const el = document.createElement("script");
            el.type = "application/ld+json";
            el.text = JSON.stringify(s);
            document.head.appendChild(el);
        });
        return () => {
            cancelAnimationFrame(frame);
            document.removeEventListener("click", onClick);
            lenis.destroy();
        };
    }, []);

    return (
        <div className="App">
            <BrowserRouter>
                <ScrollManager />
                <Header />
                <main>
                    <Suspense fallback={<PageLoader />}>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/kisan-kota-plots" element={<KisanKota />} />
                            <Route path="/yeida-authority-plots" element={<AuthorityPlots />} />
                            <Route path="/sectors" element={<SectorsHub />} />
                            <Route path="/development" element={<Development />} />
                            <Route path="/investor-guide" element={<InvestorGuide />} />
                            <Route path="/kisan-kota-vs-authority-plots" element={<KisanVsAuthority />} />
                            <Route path="/plot-transfer-process" element={<PlotTransferProcess />} />
                            <Route path="/yeida-plot-scheme" element={<RpsScheme />} />
                            <Route path="/yeida-rules-2026" element={<YeidaRules />} />
                            <Route path="/yeida-rules-2026/ptl-permission-to-lease" element={<Ptl />} />
                            <Route path="/yeida-rules-2026/transfer-memorandum-process" element={<TransferMemorandum />} />
                            <Route path="/yeida-rules-2026/transfer-charges-cost" element={<TransferCharges />} />
                            <Route path="/sectors/:slug" element={<SectorPage />} />
                            <Route path="/investment/jewar-airport-impact" element={<JewarImpact />} />
                            <Route path="/buying-guides" element={<BuyingGuides />} />
                            <Route path="/listings" element={<Listings />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                            <Route path="/terms-conditions" element={<TermsConditions />} />
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/faq" element={<FaqPage />} />
                            <Route path="/blog/:slug" element={<BlogPost />} />
                            <Route path="/admin" element={<AdminBlog />} />
                            <Route path="/admin/leads" element={<LeadDashboard />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Suspense>
                </main>
                <Footer />
                <ChatWidget />
                <WhatsAppFloat />
                <CookieConsent />
                <ExitIntentPopup />
                <StickyMobileCTA />
                <Toaster position="top-center" richColors />
            </BrowserRouter>
        </div>
    );
}

export default App;
